import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Wrench, Loader2, Key } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import portalApi from '@/lib/portalApi';
import { toast } from 'sonner';

const maskCPF = (v) => v.replace(/\D/g, '').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2').slice(0, 14);

export default function ClientePortalLogin() {
  const [cpf, setCpf] = useState('');
  const [senha, setSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => {
    if (localStorage.getItem('cliente_portal_token')) {
      navigate('/oficina/cliente/home', { replace: true });
      return;
    }
    // Se vier com token de link mágico, tentar login automático
    const t = params.get('t');
    if (t) {
      setLoading(true);
      portalApi.post('/cliente/auth/token-login', { token: t })
        .then(({ data }) => {
          localStorage.setItem('cliente_portal_token', data.access_token);
          localStorage.setItem('cliente_portal_user', JSON.stringify(data.cliente));
          toast.success(`Bem-vindo, ${data.cliente.nome}!`);
          navigate('/oficina/cliente/home', { replace: true });
        })
        .catch(() => {
          toast.error('Link inválido ou expirado. Use seu CPF e senha.');
          setLoading(false);
        });
    }
  }, [navigate, params]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await portalApi.post('/cliente/auth/login', { cpf, senha });
      localStorage.setItem('cliente_portal_token', data.access_token);
      localStorage.setItem('cliente_portal_user', JSON.stringify(data.cliente));
      toast.success(`Bem-vindo, ${data.cliente.nome}!`);
      navigate('/oficina/cliente/home');
    } catch {
      toast.error('CPF ou senha inválidos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 md:p-8 border border-blue-500/20 shadow-2xl">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-700 rounded-2xl mx-auto flex items-center justify-center mb-3 shadow-xl shadow-blue-500/30">
              <Wrench className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">Portal do Cliente</h1>
            <p className="text-blue-200/70 text-sm">Acompanhe seus serviços</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label className="text-blue-100 text-sm">CPF</Label>
              <Input
                data-testid="portal-cpf-input"
                value={cpf}
                onChange={(e) => setCpf(maskCPF(e.target.value))}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                placeholder="000.000.000-00"
                required
              />
            </div>
            <div>
              <Label className="text-blue-100 text-sm">Senha</Label>
              <Input
                data-testid="portal-senha-input"
                type="password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
                placeholder="Sua senha"
                required
              />
            </div>
            <Button
              data-testid="portal-login-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white font-semibold py-5 shadow-lg"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'ENTRAR'}
            </Button>
          </form>

          <div className="mt-5 pt-5 border-t border-white/10 text-center">
            <p className="text-blue-200/60 text-xs flex items-center justify-center gap-2">
              <Key className="w-3 h-3" /> Sem senha? Peça à oficina um link de acesso rápido
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
